import { Component, inject, signal } from '@angular/core'
import { DashboardContentComponent } from './dashboard-content.component'
import { DashboardComponent, DashboardData } from './dashboard.component'
import { PageTabsComponent, Tab } from '../page-tabs/page-tabs.component'
import { PublishedDetailsFile, PublishedDetailsReviewFilesComponent } from './published-details-review-files.component'
import { map, switchMap, tap } from 'rxjs'
import { MDMASettlementsService } from '../settlements/mdma-settlements.service'
import { ActivatedRoute } from '@angular/router'
import { ToastService } from '../toasts/toasts.service'
import { DashboardConfig } from '../../shared/models/dashboardConfig'

type TabKey = 'dashboard' | 'files'

const initialTabs: readonly Tab<TabKey>[] = [
  { value: 'dashboard', display: 'Published Details' },
  { value: 'files', display: 'Generated Files' },
]

@Component({
  selector: 'app-default-dashboard',
  imports: [
    DashboardComponent,
    DashboardContentComponent,
    PageTabsComponent,
    PublishedDetailsReviewFilesComponent,
  ],
  template: `
    <app-dashboard (onData)="setData($event)">
      @if (data()) {
        <div class="content-wrapper">
          <app-page-tabs [tabs]="tabs()" [selectedTab]="selectedTab()" (selectTab)="selectedTab.set($event)" />
          @switch (selectedTab()) {
            @case ('files') {
              <app-published-details-review-files [files]="files()" [filesLoading]="filesLoading()" />
            }
            @case ('dashboard') {
              <app-dashboard-content [title]="data()!.config.display" [url]="data()!.url" />
            }
          }
        </div>
      }
    </app-dashboard>
  `,
  styles: `
    .content-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
      margin-left: 1rem;
      margin-right: 1rem;
      
      app-dashboard-content, app-published-details-review-files {
        flex-basis: 0;
        flex-grow: 1;
      }
    }
  `,
})
export class PublishedDetailsReviewDashboardComponent {
  private readonly settlementService = inject(MDMASettlementsService)
  private readonly route = inject(ActivatedRoute)
  private readonly toast = inject(ToastService)

  protected readonly data = signal<DashboardData | undefined>(undefined)
  protected readonly tabs = signal<readonly Tab<TabKey>[]>(initialTabs)
  protected readonly selectedTab = signal<TabKey>(this.tabs()[0].value)
  protected readonly files = signal<PublishedDetailsFile[]>([])
  protected readonly filesLoading = signal(false)

  protected setData(data: DashboardData) {
    if (!this.data()) {
      this.loadFiles(data.config)
    }
    this.data.set(data)
  }

  private loadFiles(dashboard: DashboardConfig) {
    const param = dashboard.params.find(_ =>
      _.widget_friendly === 'primary_run_id',
    )
    const runIdParam = param?.widget

    if (runIdParam) {
      this.route.queryParamMap.pipe(
        map(paramsMap => {
          const runInstanceUid = paramsMap.get(runIdParam)
          if (runInstanceUid) {
            return runInstanceUid
          } else {
            throw new Error(`Dashboard parameter value for ${runIdParam} was not found`)
          }
        }),
        tap(() => {
          this.filesLoading.set(true)
        }),
        switchMap(runInstanceUid =>
          this.settlementService.getPublishedMDMAFiles(runInstanceUid).pipe(
            map((files): PublishedDetailsFile[] =>
              files.map(fileName => ({ fileName, runInstanceUid }))),
          ),
        ),
      ).subscribe({
        next: files => {
          this.files.set(files)
          this.filesLoading.set(false)
          this.updateTabs(files.length)
        },
        error: err => {
          this.filesLoading.set(false)
          this.toast.showError(err)
        },
      })
    } else {
      this.toast.show({
        type: 'error',
        message: 'Unable to find run ID parameter definition in dashboard configuration',
      })
    }
  }

  protected updateTabs(fileCount: number) {
    this.tabs.set(initialTabs.map(t =>
      t.value === 'files' ? { ...t, display: `${t.display} (${fileCount})` } : t,
    ))
  }
}
